import { Plus, Split, Trash2, Undo2 } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { DeliverySummary, PaymentMethodSummary } from '@aeris/contracts'
import { parseAmount } from '@aeris/finance'
import {
  add,
  compare,
  formatMoney,
  isPositive,
  subtract,
  toString,
  ZERO_MONEY,
  type Decimal,
} from '@aeris/money'
import { formatDocumentNumber } from '@aeris/printing'
import { Badge, Button, MoneyInput, Select, cn } from '@aeris/ui'

import {
  splitMethodsFor,
  streetMethodsFor,
  type SettlePart,
} from '../lib/street-methods.ts'

/**
 * Uma entrega na tela do caixa: "como o cliente pagou?" — F161.
 *
 * O dono: *"os motoboy saem com os pedidos mas ninguém sabe como o cliente vai
 * pagar; às vezes paga no dinheiro, ou parte dinheiro parte cartão, daí o
 * motoboy vai falar como cada um pagou e acertar"*.
 *
 * Dois caminhos, do mais comum para o menos:
 *
 * - **uma forma só** — um clique no botão da forma e uma confirmação na mesma
 *   linha. A confirmação existe porque o clique errado grava dinheiro no caixa;
 *   ela fica na linha, e não num diálogo, porque o motoboy está no balcão com
 *   quinze comprovantes;
 * - **Dividir** — as formas e os valores, com o que falta calculado na hora.
 *   Confirmado uma vez, gravado numa transação só.
 */

/** Vazio é zero: é o campo de valor antes de a pessoa digitar. */
const money = (wire: string): Decimal =>
  wire.trim() === '' ? ZERO_MONEY : parseAmount(wire)

const hour = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
})

export const DeliverySettleRow = ({
  delivery,
  methods,
  showCourier,
  busy,
  canSettle,
  onSettle,
  onReturned,
}: {
  delivery: DeliverySummary
  methods: readonly PaymentMethodSummary[]
  showCourier: boolean
  busy: boolean
  canSettle: boolean
  onSettle: (parts: readonly SettlePart[]) => void
  onReturned: () => void
}): ReactNode => {
  /*
   * O que se oferece NESTA linha (F179).
   *
   * As formas da rua para todo mundo, mais o vale e o crediário quando o
   * cliente desta entrega tem com que pagar. Quem recusa de verdade continua
   * sendo o servidor.
   */
  const purse = {
    outstanding: delivery.outstanding,
    voucherBalance: delivery.voucherBalance,
    creditAvailable: delivery.creditAvailable,
  }
  const offered = streetMethodsFor(methods, purse)
  /*
   * As partes do "Dividir" têm a lista delas (F211): o vale entra com qualquer
   * saldo, porque ali a pessoa escreve quanto usa. Com a lista dos botões, o
   * aviso mandava dividir e o vale não estava lá para escolher.
   */
  const splitOffered = splitMethodsFor(methods, purse)
  /*
   * Por que o vale (ou o crediário) não está entre os botões — F179.
   *
   * Três respostas possíveis, e a pior é o silêncio: a loja não tem a forma
   * cadastrada, o saldo não cobre o que falta, ou o cliente não tem nada. As
   * duas primeiras viram frase; a terceira não precisa de nenhuma.
   */
  const avisos = ((): readonly string[] => {
    const reasons: string[] = []
    const has = (kind: PaymentMethodSummary['kind']): boolean =>
      methods.some((method) => method.kind === kind)
    const offeredKind = (kind: PaymentMethodSummary['kind']): boolean =>
      offered.some((entry) => entry.method.kind === kind)

    const saldo = money(delivery.voucherBalance)
    if (saldo.units > 0n && !offeredKind('voucher')) {
      reasons.push(
        has('voucher')
          ? `Vale de ${formatMoney(saldo)} não cobre os ${formatMoney(money(delivery.outstanding))} — em "Dividir" ele já vem na primeira parte, e o resto vai em outra forma.`
          : `Cliente tem ${formatMoney(saldo)} de vale: cadastre uma forma do tipo Vale em Formas de pagamento.`
      )
    }

    const limite = money(delivery.creditAvailable)
    if (limite.units > 0n && !offeredKind('customer_credit')) {
      reasons.push(
        has('customer_credit')
          ? `Limite livre de ${formatMoney(limite)} não cobre o que falta.`
          : `Cliente tem ${formatMoney(limite)} de limite: cadastre uma forma do tipo Crediário para deixar na conta.`
      )
    }

    return reasons
  })()

  const outstanding = money(delivery.outstanding)
  const [confirming, setConfirming] = useState<PaymentMethodSummary | null>(
    null
  )
  const [split, setSplit] = useState<SettlePart[] | null>(null)

  const splitTotal = (split ?? []).reduce(
    (sum, part) => add(sum, money(part.amount)),
    ZERO_MONEY
  )
  const missing = subtract(outstanding, splitTotal)
  const splitValid =
    split !== null &&
    split.every(
      (part) => part.paymentMethodId !== '' && isPositive(money(part.amount))
    ) &&
    compare(splitTotal, outstanding) <= 0 &&
    isPositive(splitTotal)

  const label = `Pedido nº ${formatDocumentNumber(delivery.number, '')}`

  return (
    <li
      className="flex flex-col gap-2 rounded-(--radius-md) border border-(--border-default) bg-(--surface-raised) px-3 py-3"
      aria-label={`${label} · ${delivery.customerName}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-(--weight-semibold)">
            <span className="font-(--font-mono)">
              {formatDocumentNumber(delivery.number)}
            </span>{' '}
            · {delivery.customerName}
          </span>
          <span className="text-(length:--text-xs) text-(--text-muted)">
            {delivery.customerDistrict === null
              ? ''
              : `${delivery.customerDistrict} · `}
            {delivery.dispatchedAt === null
              ? 'saída sem hora'
              : `saiu às ${hour.format(new Date(delivery.dispatchedAt))}`}
            {showCourier && delivery.courierName !== null
              ? ` · ${delivery.courierName}`
              : ''}
          </span>
        </div>
        <span className="flex items-center gap-2 font-(--font-mono) text-(length:--text-lg) font-(--weight-semibold) tabular-nums">
          {/*
            Pago pela metade: já entrou dinheiro e ainda falta (F248). Testava
            `entryStatus === 'partial'`, um estado que o título não tem — o
            selo nunca acendeu.
          */}
          {delivery.settlements.length > 0 && isPositive(outstanding) ? (
            <Badge tone="warning">parcial</Badge>
          ) : null}
          {formatMoney(outstanding)}
        </span>
      </div>

      {!canSettle ? null : confirming !== null ? (
        <div
          role="group"
          aria-label="Confirmar recebimento"
          className="flex flex-wrap items-center gap-2 rounded-(--radius-sm) bg-(--surface-sunken) px-2 py-2"
        >
          <span className="text-(length:--text-sm)">
            Receber <strong>{formatMoney(outstanding)}</strong> em{' '}
            <strong>{confirming.name}</strong>?
          </span>
          <div className="ml-auto flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setConfirming(null)
              }}
            >
              Voltar
            </Button>
            <Button
              variant="primary"
              size="sm"
              autoFocus
              loading={busy}
              onClick={() => {
                onSettle([
                  {
                    paymentMethodId: confirming.id,
                    amount: delivery.outstanding,
                  },
                ])
                setConfirming(null)
              }}
            >
              Confirmar
            </Button>
          </div>
        </div>
      ) : split !== null ? (
        <div
          role="group"
          aria-label="Dividir o pagamento"
          className="flex flex-col gap-2 rounded-(--radius-sm) bg-(--surface-sunken) px-2 py-2"
        >
          {split.map((part, index) => (
            <div key={index} className="flex flex-wrap items-center gap-2">
              <Select
                aria-label={`Forma ${String(index + 1)}`}
                value={part.paymentMethodId}
                className="w-44"
                onChange={(event) => {
                  const value = event.target.value
                  setSplit((current) =>
                    (current ?? []).map((entry, at) =>
                      at === index
                        ? { ...entry, paymentMethodId: value }
                        : entry
                    )
                  )
                }}
              >
                <option value="">Escolha a forma</option>
                {splitOffered.map(({ method, label }) => (
                  <option key={method.id} value={method.id}>
                    {label}
                  </option>
                ))}
              </Select>
              <MoneyInput
                aria-label={`Valor ${String(index + 1)}`}
                value={part.amount}
                className="w-32"
                onValueChange={(value) => {
                  setSplit((current) =>
                    (current ?? []).map((entry, at) =>
                      at === index ? { ...entry, amount: value } : entry
                    )
                  )
                }}
              />
              {split.length > 1 ? (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Tirar a forma ${String(index + 1)}`}
                  leadingIcon={
                    <Trash2 aria-hidden="true" className="h-4 w-4" />
                  }
                  onClick={() => {
                    setSplit((current) =>
                      (current ?? []).filter((_, at) => at !== index)
                    )
                  }}
                />
              ) : null}
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
              disabled={split.length >= 6}
              onClick={() => {
                setSplit((current) => [
                  ...(current ?? []),
                  {
                    paymentMethodId: '',
                    amount: isPositive(missing) ? toString(missing) : '',
                  },
                ])
              }}
            >
              Outra forma
            </Button>
            <span
              aria-live="polite"
              className={cn(
                'text-(length:--text-sm)',
                compare(splitTotal, outstanding) > 0
                  ? 'text-(--danger)'
                  : 'text-(--text-secondary)'
              )}
            >
              {compare(splitTotal, outstanding) > 0
                ? `Passou ${formatMoney(subtract(splitTotal, outstanding))} do que falta`
                : isPositive(missing)
                  ? `Falta ${formatMoney(missing)}`
                  : 'Fechou o valor'}
            </span>
            <div className="ml-auto flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSplit(null)
                }}
              >
                Voltar
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={!splitValid}
                loading={busy}
                onClick={() => {
                  onSettle(split)
                  setSplit(null)
                }}
              >
                Confirmar
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-(length:--text-xs) text-(--text-muted)">
            Como pagou:
          </span>
          {/*
            Todas, e não as quatro primeiras (F179).

            O `slice(0, 4)` escondia a quinta forma sem dizer nada — e a quinta
            passou a ser justamente o vale do cliente. Uma forma cadastrada que
            não aparece é uma forma que o caixa jura não existir.
          */}
          {offered.map(({ method, label }) => (
            <Button
              key={method.id}
              variant="secondary"
              size="sm"
              disabled={delivery.entryId === null || busy}
              onClick={() => {
                setConfirming(method)
              }}
            >
              {label}
            </Button>
          ))}
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<Split aria-hidden="true" className="h-4 w-4" />}
            disabled={delivery.entryId === null || busy}
            onClick={() => {
              /*
               * Com vale que não cobre tudo, ele já vem na primeira parte, com
               * o saldo inteiro, e o resto na segunda (F211) — é por isso que
               * a pessoa abriu o Dividir.
               */
              const voucher = splitOffered.find(
                (entry) => entry.method.kind === 'voucher'
              )
              const saldo = money(delivery.voucherBalance)
              if (
                voucher !== undefined &&
                isPositive(saldo) &&
                compare(saldo, outstanding) < 0
              ) {
                setSplit([
                  {
                    paymentMethodId: voucher.method.id,
                    amount: toString(saldo),
                  },
                  {
                    paymentMethodId: offered[0]?.method.id ?? '',
                    amount: toString(subtract(outstanding, saldo)),
                  },
                ])
                return
              }
              setSplit([
                { paymentMethodId: offered[0]?.method.id ?? '', amount: '' },
                { paymentMethodId: offered[1]?.method.id ?? '', amount: '' },
              ])
            }}
          >
            Dividir
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            leadingIcon={<Undo2 aria-hidden="true" className="h-4 w-4" />}
            disabled={busy}
            onClick={onReturned}
          >
            Voltou com a peça
          </Button>
          {/*
            O cliente tem saldo, e a loja não tem onde encaixá-lo (F179).

            O dono olhou a tela e disse "ainda não apareceu a opção vale, e ele
            tem os dois". Há duas razões possíveis para o botão faltar, e a
            tela tem de dizer QUAL: ou a forma de pagamento não existe no
            cadastro da loja, e é isto aqui, ou o saldo não cobre o que falta —
            e aí o valor aparece abaixo, sem botão, que é a mesma informação
            dita pelo número.
          */}
          {avisos.length === 0 ? null : (
            <p className="w-full text-(length:--text-xs) text-(--text-muted)">
              {avisos.join(' · ')}
            </p>
          )}
        </div>
      )}
    </li>
  )
}
